import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import Cropper from 'react-easy-crop'
import { motion, AnimatePresence } from 'framer-motion'
import {
    Crop, RotateCw, FlipHorizontal, FlipVertical, Sparkles,
    RotateCcw, Check, Undo2, Loader2, ChevronLeft
} from 'lucide-react'
import {
    FILTER_PRESETS,
    ASPECT_RATIOS,
    generateEditedImage,
    generateThumbnail,
    loadImage
} from './EditorFilters'

/**
 * ImageEditor - A reusable image editor component with crop, rotate, flip, and filter capabilities.
 * 
 * Opens as a BottomSheet overlay. After editing, returns the edited image via the `onSave` callback.
 * Can be used by any component that needs image editing before upload.
 * 
 * @param {boolean} isOpen - Controls editor visibility
 * @param {function} onClose - Callback when editor is closed without saving
 * @param {function} onSave - Callback with edited image: ({ dataURL, file, blob }) => void
 * @param {string} imageSrc - Source image to edit (data URL or URL)
 * @param {string} outputFormat - Output MIME type (default: 'image/jpeg')
 */
const ImageEditor = ({ isOpen, onClose, onSave, imageSrc, outputFormat = 'image/jpeg' }) => {

    // ─── Active Tab ───────────────────────────────────────
    const [activeTab, setActiveTab] = useState('filters')

    // ─── Crop State ───────────────────────────────────────
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    const [aspectRatio, setAspectRatio] = useState(undefined) // free crop

    // ─── Rotate State ─────────────────────────────────────
    const [rotation, setRotation] = useState(0)

    // ─── Flip State ───────────────────────────────────────
    const [flip, setFlip] = useState({ horizontal: false, vertical: false })

    // ─── Filter State ─────────────────────────────────────
    const [selectedFilter, setSelectedFilter] = useState('original')
    const [filterThumbnail, setFilterThumbnail] = useState(null)

    // ─── No-Crop flag: when true, full image is used on Apply (no cropping) ──
    const [noCrop, setNoCrop] = useState(true)

    // ─── Processing State ─────────────────────────────────
    const [isProcessing, setIsProcessing] = useState(false)

    // Generate thumbnail for filter previews when image loads
    useEffect(() => {
        if (imageSrc && isOpen) {
            generateThumbnail(imageSrc, 64).then(setFilterThumbnail).catch(() => { })
        }
    }, [imageSrc, isOpen])

    // Reset state when editor opens with new image
    useEffect(() => {
        if (isOpen) {
            setCrop({ x: 0, y: 0 })
            setZoom(1)
            setRotation(0)
            setFlip({ horizontal: false, vertical: false })
            setSelectedFilter('original')
            setAspectRatio(undefined)
            setNoCrop(true)
            setActiveTab('filters')
        }
    }, [isOpen, imageSrc])

    // ─── Callbacks ────────────────────────────────────────

    const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
        setCroppedAreaPixels(croppedAreaPx)
    }, [])

    const handleRotate90 = useCallback((direction) => {
        setRotation(prev => {
            const newRot = direction === 'cw' ? prev + 90 : prev - 90
            return ((newRot % 360) + 360) % 360
        })
    }, [])

    const handleFlipHorizontal = useCallback(() => {
        setFlip(prev => ({ ...prev, horizontal: !prev.horizontal }))
    }, [])

    const handleFlipVertical = useCallback(() => {
        setFlip(prev => ({ ...prev, vertical: !prev.vertical }))
    }, [])

    const handleReset = useCallback(() => {
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setRotation(0)
        setFlip({ horizontal: false, vertical: false })
        setSelectedFilter('original')
        setAspectRatio(undefined)
        setNoCrop(true)
    }, [])

    const handleApply = useCallback(async () => {
        if (!imageSrc) return

        setIsProcessing(true)
        try {
            const filterCSS = FILTER_PRESETS.find(f => f.id === selectedFilter)?.filter || 'none'

            // When noCrop is true: use the full image dimensions, skip the crop box entirely
            let cropAreaToUse = croppedAreaPixels
            if (noCrop || !croppedAreaPixels) {
                const img = await loadImage(imageSrc)
                cropAreaToUse = { x: 0, y: 0, width: img.naturalWidth || img.width, height: img.naturalHeight || img.height }
            }

            const result = await generateEditedImage(
                imageSrc,
                cropAreaToUse,
                rotation,
                flip,
                filterCSS,
                outputFormat
            )
            onSave(result)
        } catch (error) {
            console.error('Image editing failed:', error)
        } finally {
            setIsProcessing(false)
        }
    }, [noCrop, croppedAreaPixels, imageSrc, rotation, flip, selectedFilter, outputFormat, onSave])

    // Get the CSS filter for the current preview
    const currentFilterCSS = useMemo(() => {
        const preset = FILTER_PRESETS.find(f => f.id === selectedFilter)
        return preset?.filter || 'none'
    }, [selectedFilter])

    // ─── Mount state for SSR safety ───────────────────────
    const [mounted, setMounted] = useState(false)
    useEffect(() => { setMounted(true) }, [])

    // ─── Tab Definitions (order: Filters, Crop, Rotate, Flip) ─
    const tabs = [
        { id: 'filters', label: 'Filters', icon: Sparkles },
        { id: 'crop',    label: 'Crop',    icon: Crop },
        { id: 'rotate',  label: 'Rotate',  icon: RotateCw },
        { id: 'flip',    label: 'Flip',    icon: FlipHorizontal },
    ]

    // ─── Reset to "no crop" (full image) ─────────────────
    const selectNoCrop = useCallback(() => {
        setAspectRatio(undefined)
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setNoCrop(true)
    }, [])

    // ─── Select a specific aspect ratio (enables crop mode) ──
    const selectAspectRatio = useCallback((value) => {
        setAspectRatio(value)
        setNoCrop(false)
    }, [])

    if (!imageSrc || !mounted) return null

    return createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
                key="image-editor"
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                className="fixed inset-0 z-[60] flex flex-col bg-black"
                style={{ touchAction: 'none' }}
            >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100 shrink-0">
                <button
                    onClick={onClose}
                    className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-sm font-medium">Cancel</span>
                </button>
                <h2 className="text-base font-semibold text-gray-900">Edit Photo</h2>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleReset}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                        title="Reset all edits"
                    >
                        <Undo2 className="w-4 h-4" />
                    </button>
                    <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={handleApply}
                        disabled={isProcessing}
                        className="flex items-center gap-1.5 px-4 py-2 bg-linear-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold rounded-full shadow-md shadow-orange-200 disabled:opacity-50 transition-all"
                    >
                        {isProcessing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <Check className="w-4 h-4" />
                                Apply
                            </>
                        )}
                    </motion.button>
                </div>
            </div>

            {/* Preview Area — flex-1 fills all remaining vertical space
                - When noCrop=true: plain <img> so no crop box appears
                - When noCrop=false: react-easy-crop with crop box
                Flip transform is applied to the whole wrapper in both cases.
            */}
            <div
                className="relative flex-1 bg-black flex items-center justify-center overflow-hidden"
                style={{
                    transform: [
                        flip.horizontal ? 'scaleX(-1)' : '',
                        flip.vertical ? 'scaleY(-1)' : ''
                    ].filter(Boolean).join(' ') || undefined
                }}
            >
                {noCrop ? (
                    /* Plain image preview — no crop box, full image visible */
                    <img
                        src={imageSrc}
                        alt="Preview"
                        className="max-w-full max-h-full object-contain"
                        style={{
                            filter: currentFilterCSS,
                            transform: `rotate(${rotation}deg)`,
                            transition: 'transform 0.2s ease',
                        }}
                    />
                ) : (
                    /* Crop mode — react-easy-crop with crop box */
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={aspectRatio}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onRotationChange={(r) => setRotation(Math.round(r))}
                        onCropComplete={onCropComplete}
                        style={{
                            containerStyle: { position: 'absolute', inset: 0 },
                            mediaStyle: { filter: currentFilterCSS }
                        }}
                        showGrid={activeTab === 'crop'}
                        objectFit="contain"
                    />
                )}
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-100 bg-white">
                {tabs.map(tab => {
                    const Icon = tab.icon
                    const isActive = activeTab === tab.id
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors relative
                                ${isActive ? 'text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <Icon className="w-5 h-5" />
                            {tab.label}
                            {isActive && (
                                <motion.div
                                    layoutId="activeEditorTab"
                                    className="absolute bottom-0 left-2 right-2 h-0.5 bg-orange-500 rounded-full"
                                />
                            )}
                        </button>
                    )
                })}
            </div>

            {/* Tab Content */}
            <div className="bg-white overflow-x-auto">
                <AnimatePresence mode="wait">
                    {/* ─── Crop Controls ─── */}
                    {activeTab === 'crop' && (
                        <motion.div
                            key="crop"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="px-4 py-3"
                        >
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xs text-gray-500 font-medium">Aspect Ratio:</span>
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {/* None = full image, no crop — default */}
                                <button
                                    onClick={selectNoCrop}
                                    className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all
                                        ${noCrop
                                            ? 'bg-orange-500 text-white shadow-md shadow-orange-200'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    None
                                </button>
                                {ASPECT_RATIOS.filter(r => r.id !== 'free').map(ratio => (
                                    <button
                                        key={ratio.id}
                                        onClick={() => selectAspectRatio(ratio.value)}
                                        className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all
                                            ${!noCrop && ratio.value === aspectRatio
                                                ? 'bg-orange-500 text-white shadow-md shadow-orange-200'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        {ratio.name}
                                    </button>
                                ))}
                            </div>
                            {/* Zoom slider */}
                            <div className="mt-4 flex items-center gap-3">
                                <span className="text-xs text-gray-500 font-medium w-10">Zoom</span>
                                <input
                                    type="range"
                                    min={1}
                                    max={3}
                                    step={0.01}
                                    value={zoom}
                                    onChange={(e) => setZoom(Number(e.target.value))}
                                    className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-orange-500"
                                />
                                <span className="text-xs text-gray-400 w-10 text-right">{zoom.toFixed(1)}x</span>
                            </div>
                        </motion.div>
                    )}

                    {/* ─── Rotate Controls ─── */}
                    {activeTab === 'rotate' && (
                        <motion.div
                            key="rotate"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="px-4 py-3"
                        >
                            <div className="flex items-center justify-center gap-4 mb-4">
                                <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleRotate90('ccw')}
                                    className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-gray-100 hover:bg-gray-200 transition-colors"
                                >
                                    <RotateCcw className="w-6 h-6 text-gray-700" />
                                    <span className="text-xs text-gray-600 font-medium">90° Left</span>
                                </motion.button>
                                <div className="flex flex-col items-center gap-1">
                                    <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center border-2 border-orange-200">
                                        <span className="text-sm font-bold text-orange-600 leading-none">{Math.round(rotation)}°</span>
                                    </div>
                                </div>
                                <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleRotate90('cw')}
                                    className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-gray-100 hover:bg-gray-200 transition-colors"
                                >
                                    <RotateCw className="w-6 h-6 text-gray-700" />
                                    <span className="text-xs text-gray-600 font-medium">90° Right</span>
                                </motion.button>
                            </div>
                            {/* Fine rotation slider */}
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500 font-medium w-16">Fine tune</span>
                                <input
                                    type="range"
                                    min={0}
                                    max={360}
                                    step={1}
                                    value={rotation}
                                    onChange={(e) => setRotation(Number(e.target.value))}
                                    className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-orange-500"
                                />
                                <span className="text-xs text-gray-400 w-10 text-right">{rotation}°</span>
                            </div>
                        </motion.div>
                    )}

                    {/* ─── Flip Controls ─── */}
                    {activeTab === 'flip' && (
                        <motion.div
                            key="flip"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="px-4 py-3"
                        >
                            <div className="flex items-center justify-center gap-6">
                                <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handleFlipHorizontal}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all
                                        ${flip.horizontal
                                            ? 'bg-orange-100 text-orange-600 ring-2 ring-orange-300'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    <FlipHorizontal className="w-8 h-8" />
                                    <span className="text-xs font-medium">Horizontal</span>
                                </motion.button>
                                <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handleFlipVertical}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all
                                        ${flip.vertical
                                            ? 'bg-orange-100 text-orange-600 ring-2 ring-orange-300'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    <FlipVertical className="w-8 h-8" />
                                    <span className="text-xs font-medium">Vertical</span>
                                </motion.button>
                            </div>
                        </motion.div>
                    )}

                    {/* ─── Filter Controls ─── */}
                    {activeTab === 'filters' && (
                        <motion.div
                            key="filters"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="px-4 py-3"
                        >
                            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                {FILTER_PRESETS.map(preset => {
                                    const isActive = selectedFilter === preset.id
                                    return (
                                        <button
                                            key={preset.id}
                                            onClick={() => setSelectedFilter(preset.id)}
                                            className={`shrink-0 flex flex-col items-center gap-1.5 transition-all
                                                ${isActive ? 'scale-105' : 'opacity-70 hover:opacity-100'}`}
                                        >
                                            <div className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-colors
                                                ${isActive ? 'border-orange-500 shadow-md shadow-orange-200' : 'border-transparent'}`}>
                                                {filterThumbnail ? (
                                                    <img
                                                        src={filterThumbnail}
                                                        alt={preset.name}
                                                        className="w-full h-full object-cover"
                                                        style={{ filter: preset.filter }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                                                )}
                                            </div>
                                            <span className={`text-[10px] font-medium
                                                ${isActive ? 'text-orange-600' : 'text-gray-500'}`}>
                                                {preset.name}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
    )
}

export default ImageEditor
